import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Copy, Check, ZoomIn } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const QR_URL =
  "https://customer-assets.emergentagent.com/job_aroha-rooms/artifacts/qcsbiuz8_PHOTO-2026-05-16-02-07-08.jpg";

function CopyButton({ value, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      data-testid={testId}
      className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
      onClick={() => {
        navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </Button>
  );
}

export default function PaymentInfoSection() {
  const [cfg, setCfg] = useState({ payment: {}, contact: {} });
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    api.get("/config/public").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const p = cfg.payment || {};

  return (
    <section id="payment" data-testid="payment-section" className="py-24 md:py-32 bg-secondary text-secondary-foreground">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid md:grid-cols-12 gap-12 mb-12">
          <div className="md:col-span-6">
            <p className="overline text-[#E8C8B3] mb-4">Payments</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium leading-[1.05] tracking-tight">
              All major Indian payment methods accepted.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 self-end">
            <p className="font-sans text-base text-secondary-foreground/85 leading-relaxed">
              Pay your 25% advance online — scan the UPI QR or make a direct bank transfer to our
              Federal Bank account. The balance is settled in cash or UPI at check-in.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="rounded-2xl bg-secondary-foreground/5 backdrop-blur p-8 border border-secondary-foreground/15">
            <p className="overline text-[#E8C8B3] mb-4">Bank Transfer</p>
            <dl className="grid grid-cols-[140px_1fr_auto] gap-y-3 text-sm font-sans items-center">
              <dt className="text-secondary-foreground/60">Account name</dt>
              <dd className="font-medium" data-testid="bank-holder">{p.bank_account_holder}</dd>
              <dd />
              <dt className="text-secondary-foreground/60">Bank</dt>
              <dd className="font-medium">{p.bank_name}</dd>
              <dd />
              <dt className="text-secondary-foreground/60">Account no.</dt>
              <dd className="font-mono">{p.bank_account_number}</dd>
              <CopyButton value={p.bank_account_number || ""} testId="copy-bank-acc" />
              <dt className="text-secondary-foreground/60">IFSC</dt>
              <dd className="font-mono">{p.bank_ifsc}</dd>
              <CopyButton value={p.bank_ifsc || ""} testId="copy-ifsc" />
              <dt className="text-secondary-foreground/60">Branch</dt>
              <dd className="font-medium">{p.bank_branch}</dd>
              <dd />
              <dt className="text-secondary-foreground/60">UPI ID</dt>
              <dd className="font-mono text-primary">{p.upi_id}</dd>
              <CopyButton value={p.upi_id || ""} testId="copy-upi" />
            </dl>
          </div>

          <div className="rounded-2xl bg-secondary-foreground/5 backdrop-blur p-8 border border-secondary-foreground/15 flex flex-col items-center justify-center">
            <p className="overline text-[#E8C8B3] mb-4">Scan & Pay · UPI</p>
            <button
              type="button"
              onClick={() => setZoom(true)}
              data-testid="payment-qr-zoom-trigger"
              aria-label="Enlarge QR code"
              className="group relative rounded-xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <img
                src={QR_URL}
                alt="Aroha Homes UPI QR"
                className="rounded-xl bg-background p-2 max-w-[240px] w-full transition-transform duration-500 group-hover:scale-[1.03]"
                data-testid="payment-qr"
              />
              <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-sans tracking-wider uppercase">
                <ZoomIn size={16} className="mr-2" /> Click to enlarge
              </span>
            </button>
            <p className="mt-4 font-sans text-xs text-secondary-foreground/70 text-center">
              GPay · PhonePe · Paytm · BHIM · FedMobile · Any UPI App
            </p>
          </div>
        </div>
      </div>

      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent
          data-testid="payment-qr-zoom-dialog"
          className="max-w-2xl bg-background p-4 sm:p-6 border-border"
        >
          <DialogTitle className="font-display text-2xl text-foreground text-center">
            Scan & Pay · Aroha Homestay
          </DialogTitle>
          <img
            src={QR_URL}
            alt="Aroha Homes UPI QR — enlarged"
            data-testid="payment-qr-enlarged"
            className="w-full h-auto rounded-xl mt-2"
          />
          <p className="text-center text-sm font-sans text-muted-foreground">
            UPI ID: <span className="text-primary font-medium">{cfg.payment?.upi_id || "arohahomestay10@fbl"}</span>
          </p>
        </DialogContent>
      </Dialog>
    </section>
  );
}
